import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { WorkflowCaseStatusDto } from "../../types/workflowDto";

/**
 * The waits between checks while a web-service call runs, when the job gives no retry time of its
 * own: 3, 5, 10, then 30 seconds (the last repeats), stopping at two minutes (Pooja Pujara on
 * 80430, 2026-10-16 - the screen used to re-read the whole case every 3 seconds).
 */
export const WEBSERVICE_POLL_DELAYS_MS = [3000, 5000, 10000, 30000];
export const WEBSERVICE_POLL_LIMIT_MS = 2 * 60 * 1000;
/** A queued retry is checked just after it is due, not on the dot. */
export const WEBSERVICE_RETRY_GRACE_MS = 2000;

type WorkflowJob = NonNullable<WorkflowCaseStatusDto["job"]>;

interface WorkflowWebServiceWaitNoticeProps {
  /** The canvas the case is waiting on, and the tag it was read at. */
  stageId: number;
  stageName: string;
  rowVersionTag: string;
  /** The light status read (GET instance/status); null when it failed. */
  checkStatus: () => Promise<WorkflowCaseStatusDto | null>;
  /** The full case read, made only once the case has moved; resolves false if it failed. */
  onRefresh: () => Promise<boolean>;
}

const delayFor = (attempt: number) =>
  WEBSERVICE_POLL_DELAYS_MS[Math.min(attempt, WEBSERVICE_POLL_DELAYS_MS.length - 1)];

/** "2026-10-16T15:45:00" -> "3:45 PM", by string: the stamp is already Eastern, so no Date shift. */
export const toClockTime = (stamp: string | null): string | null => {
  const match = stamp?.match(/T(\d{2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  return `${hours % 12 || 12}:${match[2]} ${hours < 12 ? "AM" : "PM"}`;
};

/** "attempt 2 of 5, next try at 3:45 PM" - what is true about the job right now, or null. */
export const describeJob = (job: WorkflowJob | null): string | null => {
  if (!job || job.attempt <= 0 || job.maxAttempts <= 0) return null;
  const attempt = `attempt ${job.attempt} of ${job.maxAttempts}`;
  if (job.status === "running") return `${attempt} is running now`;
  if (job.status === "queued") {
    const at = toClockTime(job.nextRunOn);
    return at && (job.secondsUntilNextRun ?? 0) > 0 ? `${attempt}, next try at ${at}` : `${attempt}, starting now`;
  }
  return null;
};

/**
 * Decision 6.4: a web-service step runs in the background (Epic 80425's worker), so the case waits
 * on it - seconds normally, or the job's whole retry schedule (about 23 minutes) while the other
 * system is down. The notice checks the case's light status, once on load and then on the job's own
 * schedule (just after a queued retry is due, else the backoff above), and makes the full case read
 * only once the case has moved - which unmounts this notice. It stops at two minutes, or when a
 * check fails, and offers Reload, which checks once.
 */
const WorkflowWebServiceWaitNotice: React.FC<WorkflowWebServiceWaitNoticeProps> = ({
  stageId,
  stageName,
  rowVersionTag,
  checkStatus,
  onRefresh,
}) => {
  const [polling, setPolling] = useState(true);
  const [checking, setChecking] = useState(false);
  const [job, setJob] = useState<WorkflowJob | null>(null);
  const latest = useRef({ checkStatus, onRefresh, rowVersionTag });
  latest.current = { checkStatus, onRefresh, rowVersionTag };
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  /**
   * One check. Resolves the job when the case is still waiting here, "moved" after the full read a
   * move triggers, or "failed". A tag change with the case still on this stage (a value written
   * mid-step) is also re-read, so the screen never acts on a stale tag - and then it is still
   * "waiting", so polling carries on with the new tag rather than stopping with the spinner on
   * (Pooja Pujara, PR 33163).
   */
  const checkOnce = async (): Promise<{ kind: "waiting"; job: WorkflowJob | null } | { kind: "moved" | "failed" }> => {
    const status = await latest.current.checkStatus();
    if (!status) return { kind: "failed" };
    const onThisStage =
      status.currentStageId === stageId && (status.status === "active" || status.status === "draft");
    if (onThisStage && status.rowVersionTag === latest.current.rowVersionTag) return { kind: "waiting", job: status.job };
    if (!(await latest.current.onRefresh())) return { kind: "failed" };
    if (!onThisStage) return { kind: "moved" };
    // The re-read passes the new tag down on the next render; until then, check against this one.
    latest.current.rowVersionTag = status.rowVersionTag;
    return { kind: "waiting", job: status.job };
  };

  useEffect(() => {
    if (!polling) return undefined;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const startedAt = Date.now();

    const run = async (backoffStep: number) => {
      const result = await checkOnce();
      if (!active) return;
      if (result.kind !== "waiting") {
        if (result.kind === "failed") setPolling(false);
        return;
      }
      setJob(result.job);

      // A queued retry has its own time; otherwise back off.
      const retryInMs =
        result.job?.status === "queued" && result.job.secondsUntilNextRun !== null
          ? result.job.secondsUntilNextRun * 1000 + WEBSERVICE_RETRY_GRACE_MS
          : null;
      const delay = retryInMs ?? delayFor(backoffStep);
      if (Date.now() - startedAt + delay > WEBSERVICE_POLL_LIMIT_MS) {
        setPolling(false);
        return;
      }
      timer = setTimeout(() => void run(retryInMs === null ? backoffStep + 1 : backoffStep), delay);
    };

    void run(0);
    return () => {
      active = false;
      clearTimeout(timer);
    };
    // checkOnce reads everything through refs; stageId is fixed for this notice (keyed by stage).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling]);

  // One check, not a new round.
  const handleReload = async () => {
    setChecking(true);
    const result = await checkOnce();
    if (!mounted.current) return;
    if (result.kind === "waiting") setJob(result.job);
    setChecking(false);
  };

  const progress = describeJob(job);
  const leave = "You can leave this page; the case will move on by itself and show in Instances & My Work.";

  if (polling) {
    return (
      <Alert severity="info" className="workflow-run__alert--flush" icon={<CircularProgress size={18} aria-hidden="true" />}>
        <span role="status">Running automatically…</span>{" "}
        {progress
          ? `${stageName}: ${progress}.`
          : "This step calls another system in the background. If that system doesn't respond, this can take up to about 25 minutes."}{" "}
        {leave}
      </Alert>
    );
  }

  return (
    <>
      <Alert severity="warning" className="workflow-run__alert--flush">
        This step is still running in the background{progress ? ` (${progress})` : ""}. {leave} Reload to check on it.
      </Alert>
      <Box className="workflow-run__actions workflow-run__actions--end">
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => void handleReload()}
          disabled={checking}
          startIcon={checking ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : undefined}
        >
          {checking ? "Checking…" : "Reload"}
        </Button>
      </Box>
    </>
  );
};

export default WorkflowWebServiceWaitNotice;
